/* eslint-disable @typescript-eslint/no-explicit-any */
import { STORAGE_KEY } from '@hooks/useLocalStorage';
import log from '@src/datadog';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import { BLACKLIST_AUTH_PATH, HTTPMethods } from './constants';
import featureFlagManagerForInitialization from './initializationFeatureFlags';
import UserManagerClient from './UserManagerClient';

interface ParamsProps<T1 = null, T2 = null> {
  path: string;
  method: 'get' | 'post' | 'put' | 'delete' | 'patch';
  params?: T1 | T2;
  data?: T1;
}

type AxiosOptionalConfig = Omit<
  AxiosRequestConfig,
  'url' | 'method' | 'data' | 'params'
>;

const ensureError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  return new Error(String(error));
};

const handleSsoLogin = async (
  instance: AxiosInstance,
  error: AxiosError,
  user: any,
): Promise<AxiosResponse | void> => {
  if (!user?.id_token || !user?.access_token) {
    const renewed = await (window as any).oidcRenew?.();
    if (renewed) {
      const originalRequest = error.config as InternalAxiosRequestConfig;
      const rebuiltHeaders = await buildRequestHeaders(
        originalRequest.url ?? '',
        originalRequest.headers as Record<string, string>,
      );
      originalRequest.headers = rebuiltHeaders as any;
      return await instance(originalRequest);
    } else {
      await UserManagerClient.signinRedirect();
    }
  } else if (user?.expired) {
    await UserManagerClient.signinRedirect();
  }
};

const createOnResponseError =
  (instance: AxiosInstance) =>
  async (error: AxiosError): Promise<AxiosError> => {
    const user = await UserManagerClient.getUser();
    if (error?.response?.status === 401) {
      if (!featureFlagManagerForInitialization.getIsKeycloakLoginWithSso()) {
        localStorage.removeItem(STORAGE_KEY.MARKETING_ID());

        await UserManagerClient.removeUser();
        await UserManagerClient.revokeTokens(['access_token', 'refresh_token']);
        await UserManagerClient.clearStaleState();

        if (user?.id_token) {
          await UserManagerClient.signoutRedirect({
            post_logout_redirect_uri: window.location.origin,
            id_token_hint: user.id_token,
          });
        } else {
          await UserManagerClient.signinRedirect();
        }
      } else {
        const result = await handleSsoLogin(instance, error, user);
        if (result) return result as any;
      }

      log.clearDDUser();
    } else if (user?.expired) {
      featureFlagManagerForInitialization.getIsKeycloakLoginWithSso()
        ? await handleSsoLogin(instance, error, user)
        : await UserManagerClient.signinRedirect();
    } else {
      log.sendAxiosError(error);
    }

    return Promise.reject(ensureError(error));
  };

class Service {
  private readonly httpService: AxiosInstance;
  private readonly baseURLResolver: string | (() => string);

  constructor(baseURL: string | (() => string)) {
    this.baseURLResolver = baseURL;

    const resolvedBaseURL = typeof baseURL === 'function' ? baseURL() : baseURL;

    this.httpService = axios.create({
      baseURL: resolvedBaseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.httpService.interceptors.request.use(
      (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        // Re-resolve baseURL on each request if it's a function
        if (typeof this.baseURLResolver === 'function') {
          request.baseURL = this.baseURLResolver();
        }
        return request;
      },
      (error: AxiosError): Promise<AxiosError> =>
        Promise.reject(ensureError(error)),
    );

    this.httpService.interceptors.response.use(
      (response: AxiosResponse): AxiosResponse => response,
      (error: AxiosError): Promise<AxiosError> =>
        createOnResponseError(this.httpService)(error),
    );
  }

  async fetchAPI<Params, Responses, OptionalParams = null>(
    parameters: ParamsProps<Params, OptionalParams>,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const { path: url, method, data, params } = parameters;

    const { headers, responseType } = options ?? {};

    const requestOptions: AxiosRequestConfig = {
      url,
      method,
      data: data ?? null,
      params: params ?? null,
      headers: {},
      responseType: responseType ?? 'json',
    };

    const normalizedHeaders: Record<string, string> = {};
    if (headers) {
      Object.entries(headers).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          normalizedHeaders[key] = String(value);
        }
      });
    }

    requestOptions.headers = await buildRequestHeaders(url, normalizedHeaders);

    return await this.httpService(requestOptions);
  }

  async get<Params, Responses>(
    path: string,
    params?: Params,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Get;
    return await this.fetchAPI<typeof params, Responses>(
      {
        path,
        method,
        params,
      },
      options,
    );
  }

  async post<Params, Responses>(
    path: string,
    data: Params,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Post;
    return await this.fetchAPI<typeof data, Responses>(
      {
        path,
        method,
        data,
      },
      options,
    );
  }

  async patch<Datas, Responses>(
    path: string,
    data: Datas,
    params?: Datas,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Patch;
    return await this.fetchAPI<typeof data, Responses>(
      {
        path,
        method,
        data,
        params,
      },
      options,
    );
  }

  async put<Datas, Responses, Params = null>(
    path: string,
    data?: Datas,
    params?: Datas | Params,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Put;
    return await this.fetchAPI<typeof data, Responses, Params>(
      {
        path,
        method,
        data,
        params,
      },
      options,
    );
  }

  async delete<Params, Responses>(
    path: string,
    data: Params,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Delete;
    return await this.fetchAPI<typeof data, Responses>(
      {
        path,
        method,
        data,
      },
      options,
    );
  }
}

export default Service;

// ? : Shared header builder to ensure retries use the same logic as fetchAPI
const buildRequestHeaders = async (
  url: string,
  baseHeaders: Record<string, string>,
): Promise<Record<string, string>> => {
  let headers: Record<string, string> = { ...baseHeaders };

  let accessToken = import.meta.env.VITE_KEYCLOAK_MOCK_NIK ?? '';

  if (import.meta.env.VITE_KEYCLOAK_ENABLE) {
    const user = await UserManagerClient.getUser();
    accessToken = user?.access_token ?? '';
  }

  const selectedMarketingId = localStorage.getItem(STORAGE_KEY.MARKETING_ID());
  if (/agency\//.exec(url)?.length && selectedMarketingId) {
    headers = {
      ...headers,
      'x-marketing-id': selectedMarketingId.replaceAll('"', ''),
    };
  }

  if (
    accessToken &&
    !BLACKLIST_AUTH_PATH.find(pathname => url.startsWith(pathname))
  ) {
    headers =
      import.meta.env.VITE_KEYCLOAK_ENABLE !== 'true'
        ? { ...headers, 'x-nik': accessToken }
        : { ...headers, Authorization: `Bearer ${accessToken}` };
  }

  return headers;
};
